import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const emptyRegla = () => ({
  id: 0,
  orden: 1,
  nombre: '',
  activo: true,
  pesoMinKg: '',
  pesoMaxKg: '',
  bultosMin: '',
  bultosMax: '',
  altoCmMax: '',
  anchoCmMax: '',
  largoCmMax: '',
  tipoCalculo: 'FIJO',
  precioBase: '',
  precioExcedentePorUnidad: '',
  minimoFacturable: '',
  precioBaseRetiro: '',
  precioExcedenteRetiro: '',
  bultosExcedenteDesde: '',
  tipoCalculoExcedente: '',
  bultosTope: '',
  precioTope: '',
  precioTopeRetiro: '',
  pesoMaxPorBultoKg: '',
  precioExcedentePorBultoKg: '',
});

function toNullableNumber(value) {
  if (value === '' || value === null || value === undefined) {
    return null;
  }

  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function toInputNumber(value) {
  return value === null || value === undefined ? '' : String(value);
}

function toRuleForm(rule) {
  return {
    id: rule.id || 0,
    orden: rule.orden ?? 1,
    nombre: rule.nombre || '',
    activo: !!rule.activo,
    pesoMinKg: toInputNumber(rule.pesoMinKg),
    pesoMaxKg: toInputNumber(rule.pesoMaxKg),
    bultosMin: toInputNumber(rule.bultosMin),
    bultosMax: toInputNumber(rule.bultosMax),
    altoCmMax: toInputNumber(rule.altoCmMax),
    anchoCmMax: toInputNumber(rule.anchoCmMax),
    largoCmMax: toInputNumber(rule.largoCmMax),
    tipoCalculo: rule.tipoCalculo || 'FIJO',
    precioBase: toInputNumber(rule.precioBase),
    precioExcedentePorUnidad: toInputNumber(rule.precioExcedentePorUnidad),
    minimoFacturable: toInputNumber(rule.minimoFacturable),
    precioBaseRetiro: toInputNumber(rule.precioBaseRetiro),
    precioExcedenteRetiro: toInputNumber(rule.precioExcedenteRetiro),
    bultosExcedenteDesde: toInputNumber(rule.bultosExcedenteDesde),
    tipoCalculoExcedente: rule.tipoCalculoExcedente || '',
    bultosTope: toInputNumber(rule.bultosTope),
    precioTope: toInputNumber(rule.precioTope),
    precioTopeRetiro: toInputNumber(rule.precioTopeRetiro),
    pesoMaxPorBultoKg: toInputNumber(rule.pesoMaxPorBultoKg),
    precioExcedentePorBultoKg: toInputNumber(rule.precioExcedentePorBultoKg),
  };
}

export default function AdminContratos() {
  const { authRequestJson, logout } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [masters, setMasters] = useState([]);
  const [rules, setRules] = useState([]);
  const [masterFilters, setMasterFilters] = useState({ q: '', activo: 'true' });
  const [selectedMasterId, setSelectedMasterId] = useState(searchParams.get('maestroId') || '');
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyRegla());
  const [loading, setLoading] = useState(false);
  const [masterLoading, setMasterLoading] = useState(false);
  const [error, setError] = useState(null);

  const loadMasters = useCallback(
    async (queryFilters = {}) => {
      setMasterLoading(true);
      setError(null);

      try {
        const qs = new URLSearchParams();
        if (queryFilters.q) qs.set('q', queryFilters.q);
        if (queryFilters.activo !== '' && queryFilters.activo !== null && queryFilters.activo !== undefined) {
          qs.set('activo', queryFilters.activo);
        }

        const data = await authRequestJson(`/admin/tarifas-maestro${qs.toString() ? `?${qs}` : ''}`);
        const nextMasters = data.data || [];
        setMasters(nextMasters);

        if (!selectedMasterId && nextMasters.length > 0) {
          setSelectedMasterId(String(nextMasters[0].id));
          setSearchParams({ maestroId: String(nextMasters[0].id) });
        }
      } catch (err) {
        if (err.status === 401) {
          logout();
          setError('La sesion vencio. Volve a iniciar sesion.');
        } else {
          setError(err.message || 'Error cargando maestros');
        }
      } finally {
        setMasterLoading(false);
      }
    },
    [authRequestJson, logout, selectedMasterId, setSearchParams]
  );

  const loadRules = useCallback(
    async (masterId) => {
      if (!masterId) {
        setRules([]);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const data = await authRequestJson(`/admin/tarifas-maestro/${masterId}/reglas`);
        setRules(data.data || []);
      } catch (err) {
        if (err.status === 401) {
          logout();
          setError('La sesion vencio. Volve a iniciar sesion.');
        } else {
          setError(err.message || 'Error cargando reglas');
        }
      } finally {
        setLoading(false);
      }
    },
    [authRequestJson, logout]
  );

  useEffect(() => {
    void loadMasters(masterFilters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedMasterId) {
      void loadRules(selectedMasterId);
    } else {
      setRules([]);
    }
  }, [selectedMasterId, loadRules]);

  const selectedMaster = useMemo(
    () => masters.find((master) => String(master.id) === String(selectedMasterId)) || null,
    [masters, selectedMasterId]
  );

  const summary = useMemo(
    () => ({
      masters: masters.length,
      rulesTotal: rules.length,
      rulesActivas: rules.filter((rule) => rule.activo).length,
    }),
    [masters, rules]
  );

  const startCreate = () => {
    setForm(emptyRegla());
    setEditing(-1);
  };

  const startEdit = (rule) => {
    setForm(toRuleForm(rule));
    setEditing(rule.id || -1);
  };

  const handleSave = async (event) => {
    event.preventDefault();

    if (!selectedMasterId) {
      setError('Selecciona una tarifa maestra antes de crear o editar reglas.');
      return;
    }

    setError(null);

    const payload = {
      orden: Number(form.orden) || 0,
      nombre: form.nombre.trim() || null,
      activo: form.activo,
      pesoMinKg: toNullableNumber(form.pesoMinKg),
      pesoMaxKg: toNullableNumber(form.pesoMaxKg),
      bultosMin: toNullableNumber(form.bultosMin),
      bultosMax: toNullableNumber(form.bultosMax),
      altoCmMax: toNullableNumber(form.altoCmMax),
      anchoCmMax: toNullableNumber(form.anchoCmMax),
      largoCmMax: toNullableNumber(form.largoCmMax),
      tipoCalculo: form.tipoCalculo,
      precioBase: Number(form.precioBase) || 0,
      precioExcedentePorUnidad: toNullableNumber(form.precioExcedentePorUnidad),
      minimoFacturable: toNullableNumber(form.minimoFacturable),
      precioBaseRetiro: toNullableNumber(form.precioBaseRetiro),
      precioExcedenteRetiro: toNullableNumber(form.precioExcedenteRetiro),
      bultosExcedenteDesde: toNullableNumber(form.bultosExcedenteDesde),
      tipoCalculoExcedente: form.tipoCalculoExcedente.trim() || null,
      bultosTope: toNullableNumber(form.bultosTope),
      precioTope: toNullableNumber(form.precioTope),
      precioTopeRetiro: toNullableNumber(form.precioTopeRetiro),
      pesoMaxPorBultoKg: toNullableNumber(form.pesoMaxPorBultoKg),
      precioExcedentePorBultoKg: toNullableNumber(form.precioExcedentePorBultoKg),
    };

    const url = form.id
      ? `/admin/tarifas-maestro/${selectedMasterId}/reglas/${form.id}`
      : `/admin/tarifas-maestro/${selectedMasterId}/reglas`;
    const method = form.id ? 'PUT' : 'POST';

    try {
      const data = await authRequestJson(url, {
        method,
        body: JSON.stringify(payload),
      });

      if (!data.ok) {
        throw new Error(data.error || 'Error al guardar');
      }

      setEditing(null);
      setForm(emptyRegla());
      await loadRules(selectedMasterId);
    } catch (err) {
      if (err.status === 401) {
        logout();
        setError('La sesion vencio. Volve a iniciar sesion.');
      } else {
        setError(err.message || 'Error al guardar');
      }
    }
  };

  const handleDelete = async (ruleId) => {
    if (!selectedMasterId) {
      return;
    }

    if (!window.confirm('Eliminar esta regla?')) {
      return;
    }

    setError(null);

    try {
      const data = await authRequestJson(`/admin/tarifas-maestro/${selectedMasterId}/reglas/${ruleId}`, {
        method: 'DELETE',
      });

      if (!data.ok) {
        throw new Error(data.error || 'Error al eliminar');
      }

      await loadRules(selectedMasterId);
    } catch (err) {
      if (err.status === 401) {
        logout();
        setError('La sesion vencio. Volve a iniciar sesion.');
      } else {
        setError(err.message || 'Error al eliminar');
      }
    }
  };

  const onSelectMaster = (value) => {
    setSelectedMasterId(value);
    setSearchParams(value ? { maestroId: value } : {});
    setEditing(null);
    setForm(emptyRegla());
  };

  return (
    <div className="page-shell">
      <section className="panel admin-panel">
        <div className="panel-head">
          <div>
            <p className="eyebrow">Administracion de reglas</p>
            <h1>Reglas por tarifa maestra</h1>
            <p className="panel-copy">
              Selecciona un maestro y edita las reglas de precio reales que cuelgan de esa cabecera.
            </p>
          </div>

          <div className="panel-actions">
            <Link to="/admin" className="btn-secondary btn-inline">
              Volver al panel
            </Link>
            <Link to="/admin/tarifas" className="btn-secondary btn-inline">
              Volver a maestros
            </Link>
            <button className="btn-primary btn-inline" onClick={startCreate} type="button" disabled={!selectedMasterId}>
              + Nueva regla
            </button>
          </div>
        </div>

        <div className="stats-strip">
          <article>
            <span>Maestros</span>
            <strong>{summary.masters}</strong>
          </article>
          <article>
            <span>Reglas</span>
            <strong>{summary.rulesTotal}</strong>
          </article>
          <article>
            <span>Activas</span>
            <strong>{summary.rulesActivas}</strong>
          </article>
        </div>

        <div className="filters">
          <input
            placeholder="Buscar maestro"
            value={masterFilters.q}
            onChange={(e) => setMasterFilters((prev) => ({ ...prev, q: e.target.value }))}
          />
          <select
            value={masterFilters.activo}
            onChange={(e) => setMasterFilters((prev) => ({ ...prev, activo: e.target.value }))}
          >
            <option value="true">Activos</option>
            <option value="">Todos</option>
            <option value="false">Inactivos</option>
          </select>
          <button className="btn-secondary btn-inline" onClick={() => loadMasters(masterFilters)} type="button">
            Buscar maestros
          </button>
        </div>

        <div className="filters">
          <label className="select-label">
            Maestro seleccionado
            <select value={selectedMasterId} onChange={(e) => onSelectMaster(e.target.value)}>
              <option value="">Selecciona un maestro</option>
              {masters.map((master) => (
                <option key={master.id} value={master.id}>
                  {master.nombre}
                </option>
              ))}
            </select>
          </label>
          {selectedMaster && (
            <div className="selection-chip">
              <strong>{selectedMaster.nombre}</strong>
              <span>
                {selectedMaster.clienteNombre || 'General'} - {selectedMaster.localidadNombre || 'Sin destino'}
                {selectedMaster.localidadOrigenNombre ? ` / origen ${selectedMaster.localidadOrigenNombre}` : ''}
              </span>
            </div>
          )}
        </div>

        {error && <div className="alert error">{error}</div>}

        {editing !== null && selectedMasterId && (
          <form onSubmit={handleSave} className="form-edit">
            <h2>{form.id ? 'Editar regla' : 'Nueva regla'}</h2>

            <div className="grid-2">
              <label>
                Orden
                <input
                  type="number"
                  value={form.orden}
                  onChange={(e) => setForm((prev) => ({ ...prev, orden: e.target.value }))}
                  required
                />
              </label>
              <label>
                Nombre
                <input
                  value={form.nombre}
                  onChange={(e) => setForm((prev) => ({ ...prev, nombre: e.target.value }))}
                />
              </label>
              <label>
                Tipo calculo
                <select
                  value={form.tipoCalculo}
                  onChange={(e) => setForm((prev) => ({ ...prev, tipoCalculo: e.target.value }))}
                >
                  <option value="FIJO">FIJO</option>
                  <option value="POR_KG">POR_KG</option>
                  <option value="POR_M3">POR_M3</option>
                  <option value="POR_BULTO">POR_BULTO</option>
                  <option value="POR_UNIDAD">POR_UNIDAD</option>
                </select>
              </label>
              <label>
                Tipo excedente
                <select
                  value={form.tipoCalculoExcedente}
                  onChange={(e) => setForm((prev) => ({ ...prev, tipoCalculoExcedente: e.target.value }))}
                >
                  <option value="">Sin excedente</option>
                  <option value="FIJO">FIJO</option>
                  <option value="POR_KG">POR_KG</option>
                  <option value="POR_M3">POR_M3</option>
                  <option value="POR_BULTO">POR_BULTO</option>
                  <option value="POR_UNIDAD">POR_UNIDAD</option>
                </select>
              </label>
              <label>
                Precio base
                <input
                  type="number"
                  step="0.01"
                  value={form.precioBase}
                  onChange={(e) => setForm((prev) => ({ ...prev, precioBase: e.target.value }))}
                  required
                />
              </label>
              <label>
                Precio excedente unidad
                <input
                  type="number"
                  step="0.01"
                  value={form.precioExcedentePorUnidad}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, precioExcedentePorUnidad: e.target.value }))
                  }
                />
              </label>
              <label>
                Minimo facturable
                <input
                  type="number"
                  step="0.01"
                  value={form.minimoFacturable}
                  onChange={(e) => setForm((prev) => ({ ...prev, minimoFacturable: e.target.value }))}
                />
              </label>
              <label>
                Precio base retiro
                <input
                  type="number"
                  step="0.01"
                  value={form.precioBaseRetiro}
                  onChange={(e) => setForm((prev) => ({ ...prev, precioBaseRetiro: e.target.value }))}
                />
              </label>
              <label>
                Precio excedente retiro
                <input
                  type="number"
                  step="0.01"
                  value={form.precioExcedenteRetiro}
                  onChange={(e) => setForm((prev) => ({ ...prev, precioExcedenteRetiro: e.target.value }))}
                />
              </label>
              <label>
                Peso min kg
                <input
                  type="number"
                  step="0.01"
                  value={form.pesoMinKg}
                  onChange={(e) => setForm((prev) => ({ ...prev, pesoMinKg: e.target.value }))}
                />
              </label>
              <label>
                Peso max kg
                <input
                  type="number"
                  step="0.01"
                  value={form.pesoMaxKg}
                  onChange={(e) => setForm((prev) => ({ ...prev, pesoMaxKg: e.target.value }))}
                />
              </label>
              <label>
                Bultos min
                <input
                  type="number"
                  value={form.bultosMin}
                  onChange={(e) => setForm((prev) => ({ ...prev, bultosMin: e.target.value }))}
                />
              </label>
              <label>
                Bultos max
                <input
                  type="number"
                  value={form.bultosMax}
                  onChange={(e) => setForm((prev) => ({ ...prev, bultosMax: e.target.value }))}
                />
              </label>
              <label>
                Alto max cm
                <input
                  type="number"
                  step="0.01"
                  value={form.altoCmMax}
                  onChange={(e) => setForm((prev) => ({ ...prev, altoCmMax: e.target.value }))}
                />
              </label>
              <label>
                Ancho max cm
                <input
                  type="number"
                  step="0.01"
                  value={form.anchoCmMax}
                  onChange={(e) => setForm((prev) => ({ ...prev, anchoCmMax: e.target.value }))}
                />
              </label>
              <label>
                Largo max cm
                <input
                  type="number"
                  step="0.01"
                  value={form.largoCmMax}
                  onChange={(e) => setForm((prev) => ({ ...prev, largoCmMax: e.target.value }))}
                />
              </label>
              <label>
                Bultos excedente desde
                <input
                  type="number"
                  value={form.bultosExcedenteDesde}
                  onChange={(e) => setForm((prev) => ({ ...prev, bultosExcedenteDesde: e.target.value }))}
                />
              </label>
              <label>
                Bultos tope
                <input
                  type="number"
                  value={form.bultosTope}
                  onChange={(e) => setForm((prev) => ({ ...prev, bultosTope: e.target.value }))}
                />
              </label>
              <label>
                Precio tope
                <input
                  type="number"
                  step="0.01"
                  value={form.precioTope}
                  onChange={(e) => setForm((prev) => ({ ...prev, precioTope: e.target.value }))}
                />
              </label>
              <label>
                Precio tope retiro
                <input
                  type="number"
                  step="0.01"
                  value={form.precioTopeRetiro}
                  onChange={(e) => setForm((prev) => ({ ...prev, precioTopeRetiro: e.target.value }))}
                />
              </label>
              <label>
                Peso max por bulto kg
                <input
                  type="number"
                  step="0.01"
                  value={form.pesoMaxPorBultoKg}
                  onChange={(e) => setForm((prev) => ({ ...prev, pesoMaxPorBultoKg: e.target.value }))}
                />
              </label>
              <label>
                Precio excedente por bulto kg
                <input
                  type="number"
                  step="0.01"
                  value={form.precioExcedentePorBultoKg}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, precioExcedentePorBultoKg: e.target.value }))
                  }
                />
              </label>
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={form.activo}
                  onChange={(e) => setForm((prev) => ({ ...prev, activo: e.target.checked }))}
                />
                Activa
              </label>
            </div>

            <div className="form-actions">
              <button type="submit" className="btn-primary btn-inline">
                Guardar
              </button>
              <button type="button" className="btn-secondary btn-inline" onClick={() => setEditing(null)}>
                Cancelar
              </button>
            </div>
          </form>
        )}

        {masterLoading || loading ? (
          <p className="empty-state">Cargando reglas reales...</p>
        ) : !selectedMasterId ? (
          <p className="empty-state">Selecciona un maestro para ver y editar sus reglas.</p>
        ) : (
          <div className="table-shell">
            <table>
              <thead>
                <tr>
                  <th>Orden</th>
                  <th>Nombre</th>
                  <th>Tipo</th>
                  <th>Condicion</th>
                  <th>Precio base</th>
                  <th>Excedente</th>
                  <th>Activo</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {rules.map((rule) => (
                  <tr key={rule.id}>
                    <td>{rule.orden}</td>
                    <td>
                      <strong>{rule.nombre || `Regla ${rule.orden}`}</strong>
                    </td>
                    <td>{rule.tipoCalculo}</td>
                    <td>
                      <div className="table-muted">
                        {rule.pesoMinKg ?? '-'} - {rule.pesoMaxKg ?? '-'} kg
                      </div>
                      <div className="table-muted">
                        {rule.bultosMin ?? '-'} - {rule.bultosMax ?? '-'} bultos
                      </div>
                    </td>
                    <td>${Number(rule.precioBase || 0).toFixed(2)}</td>
                    <td>
                      <div className="table-muted">
                        {rule.precioExcedentePorUnidad !== null && rule.precioExcedentePorUnidad !== undefined
                          ? `$${Number(rule.precioExcedentePorUnidad).toFixed(2)}`
                          : '-'}
                      </div>
                      <div className="table-muted">
                        {rule.tipoCalculoExcedente || 'Sin excedente'}
                      </div>
                    </td>
                    <td>{rule.activo ? 'Si' : 'No'}</td>
                    <td>
                      <div className="row-actions">
                        <button className="btn-secondary btn-inline" onClick={() => startEdit(rule)} type="button">
                          Editar
                        </button>
                        <button
                          className="btn-secondary btn-inline danger"
                          onClick={() => handleDelete(rule.id)}
                          type="button"
                        >
                          Eliminar
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
